import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../api';

const ROLES = ['ADMIN', 'FACTURACION', 'USER'];

const MODULE_LABELS = {
  contabilidad: 'Contabilidad (pipeline + F29 admin)',
  informesIva: 'Informes IVA'
};

function ModuleEditor({ moduleKey, title, value, onChange }) {
  const v = value || { roles: [], emails: [] };
  const toggleRole = (role) => {
    const roles = v.roles.includes(role) ? v.roles.filter((r) => r !== role) : [...v.roles, role];
    onChange({ ...v, roles });
  };
  return (
    <div className="border rounded-xl p-4 bg-white shadow-sm">
      <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-xs text-slate-500 mb-3">{MODULE_LABELS[moduleKey]}</p>
      <div className="flex flex-wrap gap-3 mb-3">
        {ROLES.map((role) => (
          <label key={role} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={v.roles.includes(role)} onChange={() => toggleRole(role)} />
            {role}
          </label>
        ))}
      </div>
      <label className="block text-xs text-slate-600 mb-1">Emails adicionales (uno por línea)</label>
      <textarea
        className="w-full border rounded-lg px-3 py-2 text-sm min-h-[72px]"
        value={(v.emails || []).join('\n')}
        onChange={(e) =>
          onChange({
            ...v,
            emails: e.target.value
              .split(/\n+/)
              .map((s) => s.trim())
              .filter(Boolean)
          })
        }
        placeholder="usuario@ejemplo.cl"
      />
    </div>
  );
}

export default function NavMenuConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/nav-access/config');
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/nav-access/config', cfg);
      setCfg(data);
      toast.success('Menú actualizado. Recargue la página en otros equipos si hace falta.');
      const me = await api.get('/usuarios/me');
      window.dispatchEvent(new CustomEvent('user-nav-updated', { detail: me.data }));
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="p-6 text-slate-500">Cargando…</p>;
  if (!cfg) return <p className="p-6 text-red-600">No se pudo cargar la configuración.</p>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <Link to="/usuarios" className="text-sm text-indigo-700 hover:underline">
        ← Usuarios
      </Link>
      <h1 className="text-xl font-semibold text-slate-900 mt-2">Acceso al menú (super admin)</h1>
      <p className="text-sm text-slate-600 mt-2 mb-6">
        Los super administradores siempre ven Contabilidad e Informes IVA. Aquí define qué roles y emails extra pueden
        ver cada módulo en el navbar y usar la API. Si no marca ningún rol ni email, solo super admins tienen acceso.
      </p>

      <label className="block text-sm font-medium text-slate-800 mb-1">Super administradores</label>
      <textarea
        className="w-full border rounded-lg px-3 py-2 text-sm mb-6 min-h-[80px]"
        value={(cfg.superAdminEmails || []).join('\n')}
        onChange={(e) =>
          setCfg({
            ...cfg,
            superAdminEmails: e.target.value
              .split(/\n+/)
              .map((s) => s.trim().toLowerCase())
              .filter(Boolean)
          })
        }
      />

      <div className="space-y-4 mb-6">
        <ModuleEditor
          moduleKey="contabilidad"
          title="Contabilidad"
          value={cfg.navModules?.contabilidad}
          onChange={(contabilidad) => setCfg({ ...cfg, navModules: { ...cfg.navModules, contabilidad } })}
        />
        <ModuleEditor
          moduleKey="informesIva"
          title="Informes IVA"
          value={cfg.navModules?.informesIva}
          onChange={(informesIva) => setCfg({ ...cfg, navModules: { ...cfg.navModules, informesIva } })}
        />
      </div>

      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50"
      >
        Guardar
      </button>
    </div>
  );
}
